import { AfterContentChecked, Component, ElementRef, OnInit, ViewChild, Renderer2, Inject } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorDefaultOptions, PageEvent } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { CertificatesPendingService, FeedMessageService, UtilsService, showSuccess, UserService } from '@services/index.service';
import { SelectionModel } from '@angular/cdk/collections';
import { DialogPendingCertDeleteComponent } from './dialog-pending-cert-delete.component';
import { environment } from '@environments/environment';
import { DOCUMENT } from '@angular/common';
import { DialogPendingCertActivateComponent } from './dialog-pending-cert-activate.component';
import { OrderMode, PagedTableRequest, PagedTableResponse, PreloadedCertificate, PreloadedCertificateIds, PreloadedCertificateOrderBy, Response, PreferenceRequest, PreferenceResponse, CertificateIdsRequest, UserPreferenceListConfig, UserPreferenceSourceType, ErrorCode } from '@models/index.model';
import { concatMap, filter, finalize, map, switchMap, tap, Observable, catchError, throwError, of } from 'rxjs';

@Component({
    selector: 'app-pending-certificates',
    templateUrl: './pending-certificates.component.html',
    styles: [],
    standalone: false
})
export class PendingCertificatesComponent implements OnInit, AfterContentChecked {
  @ViewChild('listParent', { static: true }) listParent: ElementRef;
  @ViewChild('listTop', { static: true }) listTop: ElementRef;
  @ViewChild('listContent', { static: true }) listContent: ElementRef;
  listDinamycHeight: any;
  pantResolution: number;
  // App
  public theme = environment.THEME;
  public currentLang: string;

  private readonly _defaultUserPreference: UserPreferenceListConfig = {
    orderBy: PreloadedCertificateOrderBy.UPLOADED_TIME,
    orderMode: OrderMode.ASC,
    pageSize: 5,
  };
  private readonly _basePreferenceRequest: PreferenceRequest = {
    source: UserPreferenceSourceType.PersonalArea,
    key: 'pending-certificates',
    value: null,
  };
  public userPreference: UserPreferenceListConfig = null;
  private readonly _preloadedCertsRequest: PagedTableRequest = {
    filter: '',
    blockNumber: 0,
    orderBy: PreloadedCertificateOrderBy.UPLOADED_TIME,
    orderMode: OrderMode.ASC,
    itemsPerBlock: 1000,
  };
  public defaultPaginationOpts: MatPaginatorDefaultOptions = {
    pageSizeOptions: [5, 10, 20, 50, 100],
    showFirstLastButtons: false,
    pageSize: this._defaultUserPreference.pageSize,
  };

  // Spinners
  public loadingInfo: boolean = true;
  public sendingInfo: boolean = false;
  public checkAllLoading: boolean = false;

  // Table
  public displayedColumnsPreloadCert: string[] = ['id', 'alias', 'uploadedTime', 'columnAction'];
  public dataSource: MatTableDataSource<PreloadedCertificate> = new MatTableDataSource();
  public rowsCount: number;

  // Row selection
  public selection = new SelectionModel<number>(true, []);

  // Table Sorting
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  private readonly _sortDirectionToNumber = {
    asc: OrderMode.ASC,
    desc: OrderMode.DESC,
  };
  public numberToSortDirection = {
    0: 'asc',
    1: 'desc',
  };
  public numberToActiveSortField = {
    1: 'alias',
    2: 'uploadedTime',
  };
  private readonly _activeSortFieldToNumber = {
    alias: PreloadedCertificateOrderBy.ALIAS,
    uploadedTime: PreloadedCertificateOrderBy.UPLOADED_TIME,
  };

  // Table Pagination
  private paginator: MatPaginator;
  @ViewChild(MatPaginator) set paginatorChange(paginator: MatPaginator) {
    if (paginator) {
      this.paginator = paginator;
      this.dataSource.paginator = paginator;
      setTimeout(() => (this.dataSource.paginator.length = this.rowsCount));
    }
  }
  // Table Filtering
  private readonly _filterPredicate = (dataRow: PreloadedCertificate, filterValue: string) =>
    dataRow.alias.includes(filterValue);

  public constructor(
    private readonly _translate: TranslateService,
    private readonly _dialog: MatDialog,
    private readonly _notify: FeedMessageService,
    private readonly _utils: UtilsService,
    private readonly _preloadedCertificates: CertificatesPendingService,
    private readonly _userServ: UserService,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  public ngOnInit(): void {
    this.theme = this._utils.titleCaseWord(this.theme);
    this.currentLang = this._translate.currentLang;
    this.dataSource.filterPredicate = this._filterPredicate;
    this._initPreloadedCertsWithUserPreferences(this._getUserPreference()).subscribe();
  }

  ngAfterContentChecked() {
    this.pantResolution = window.innerWidth;
    if (this.listContent && (this.pantResolution >= 992)) {
      this.listDinamycHeight = this.listParent.nativeElement.offsetHeight - this.listTop.nativeElement.offsetHeight;
    }
  }

  scrollReset() {
    const table = this.document.querySelector('.matCustomTable');
    if (table) {
      this.renderer.setProperty(table, 'scrollTop', 0);
      this.renderer.setProperty(table, 'scrollLeft', 0);
    }
  }

  private _initPreloadedCertsWithUserPreferences(
    userPreference$: Observable<PreferenceResponse>
  ): Observable<PagedTableResponse<PreloadedCertificate>> {
    return userPreference$.pipe(
      map((res: PreferenceResponse) => {
        return res?.value ? this._utils.parseUserPreferenceConfig(res.value) : this._defaultUserPreference;
      }),
      catchError((err) => {
        if (err.status === 401 && err.error.errorCode === ErrorCode.UNAUTHORIZED) {
          return throwError(() => err);
        }
        return of(this._defaultUserPreference);
      }),
      concatMap((currentUserPrefrence: UserPreferenceListConfig) => {
        this.userPreference = currentUserPrefrence;
        this.defaultPaginationOpts.pageSize = currentUserPrefrence.pageSize;
        this._preloadedCertsRequest.orderBy = currentUserPrefrence.orderBy;
        this._preloadedCertsRequest.orderMode = currentUserPrefrence.orderMode;
        this._preloadedCertsRequest.blockNumber = 0;

        return this._getPreloadedCertificates(this._preloadedCertsRequest);
      })
    );
  }

  // SORTING
  public sortChange(sortChange: Sort): void {
    const currentOrderBy = this._activeSortFieldToNumber[sortChange.active];
    const currentOrderMode = this._sortDirectionToNumber[sortChange.direction];
    if (this.userPreference.orderBy !== currentOrderBy || this.userPreference.orderMode !== currentOrderMode) {
      this.userPreference.orderBy = currentOrderBy;
      this.userPreference.orderMode = currentOrderMode;
      const userPreferenceRequest = {
        ...this._basePreferenceRequest,
        value: this._utils.stringifyUserPreferenceConfig(this.userPreference),
      };

      this._preloadedCertsRequest.orderBy = currentOrderBy;
      this._preloadedCertsRequest.orderMode = currentOrderMode;
      this._preloadedCertsRequest.blockNumber = 0;

      this._resetPageIndex();

      this._getPreloadedCertificates(this._preloadedCertsRequest)
        .pipe(concatMap(_ => this._setUserPreference(userPreferenceRequest)))
        .subscribe();
    }
  }

  // FILTER
  public applyFilter(filterValue: string): void {
    this._resetPageIndex();

    this._preloadedCertsRequest.filter = filterValue?.trim().toLowerCase();
    this._preloadedCertsRequest.blockNumber = 0;

    this._getPreloadedCertificates(this._preloadedCertsRequest).subscribe();
  }

  // PAGINATION
  public paginationChange(paginationChange: PageEvent): void {
    const updatePageSize: boolean = this.userPreference.pageSize !== paginationChange.pageSize;
    let userPreferenceRequest: PreferenceRequest = null;
    if (updatePageSize) {
      this._resetPageIndex();
      this.userPreference.pageSize = paginationChange.pageSize;
      userPreferenceRequest = {
        ...this._basePreferenceRequest,
        value: this._utils.stringifyUserPreferenceConfig(this.userPreference),
      };
      this.defaultPaginationOpts.pageSize = this.userPreference.pageSize;
    }

    const currentBlockNumber: number = this._calculateBlockNumber(
      paginationChange.pageIndex,
      this.userPreference.pageSize,
      this._preloadedCertsRequest.itemsPerBlock
    );

    const updateBlockNumber: boolean = currentBlockNumber !== this._preloadedCertsRequest.blockNumber;
    this._preloadedCertsRequest.blockNumber = currentBlockNumber;

    if (updatePageSize && updateBlockNumber) {
      this._getPreloadedCertificates(this._preloadedCertsRequest)
        .pipe(concatMap(_ => this._setUserPreference(userPreferenceRequest)))
        .subscribe();
    } else {
      if (updatePageSize) {
        this._setUserPreference(userPreferenceRequest).subscribe();
      }
      if (updateBlockNumber) {
        this._getPreloadedCertificates(this._preloadedCertsRequest).subscribe();
      }
    }
    this.scrollReset();
  }

  private _resetPageIndex() {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  private _calculateBlockNumber(pageIndex: number, pageSize: number, itemsPerBlock: number): number {
    return Math.floor((pageIndex * pageSize) / itemsPerBlock);
  }

  // ROW SELECTION
  public masterToggle(): void {
    if (this.selection.selected.length === this.rowsCount) {
      this.selection.clear();
      return;
    }
    const preloadCertIdsRequest: CertificateIdsRequest = { filter: this._preloadedCertsRequest.filter };
    this.checkAllLoading = true;
    this._getPersonalCertificateIds(preloadCertIdsRequest)
      .pipe(finalize(() => (this.checkAllLoading = false)))
      .subscribe((certificateIds: number[]) => {
        this.selection.select(...certificateIds);
      });
  }

  private _getPersonalCertificateIds(preloadCertIdsRequest: CertificateIdsRequest): Observable<number[]> {
    return this._preloadedCertificates
      .getPreloadedCertificateIds(preloadCertIdsRequest)
      .pipe(map((res: Response<PreloadedCertificateIds>) => res.data.preloadedCertificateIds));
  }

  public onAtivateCert(certId: number): void {
    const dialogConfig: MatDialogConfig = {
      width: '400px',
      disableClose: true,
      data: {
        id: certId,
      },
    };

    const dialogRef = this._dialog.open(DialogPendingCertActivateComponent, dialogConfig);
    dialogRef.afterClosed().pipe(
      filter((res: boolean) => res),
      switchMap(_ => {
        this._resetPageIndex();
        this._preloadedCertsRequest.blockNumber = 0;
        return this._getPreloadedCertificates(this._preloadedCertsRequest);
      })
    ).subscribe();
  }

  public onDeleteCert(selectedCerts: Array<number>): void {
    const dialogConfig: MatDialogConfig = {
      width: '450px',
      disableClose: true,
      data: {
        size: null,
      },
    };

    dialogConfig.data.size = selectedCerts.length;

    const deletePreloadCertsRequest: PreloadedCertificateIds = {
      preloadedCertificateIds: selectedCerts,
    };

    const dialogRef = this._dialog.open(DialogPendingCertDeleteComponent, dialogConfig);
    dialogRef
      .afterClosed()
      .pipe(
        filter((res: boolean) => res),
        switchMap(_ => this._deletePreloadedCertificate(deletePreloadCertsRequest)),
        concatMap(_ => {
          this._resetPageIndex();
          this._preloadedCertsRequest.blockNumber = 0;
          return this._getPreloadedCertificates(this._preloadedCertsRequest);
        })
      )
      .subscribe();
  }

  public onDeleteGlobalCert(selectedCertificates: Array<number>): void {
    const dialogConfig: MatDialogConfig = {
      width: '450px',
      disableClose: true,
      data: {
        alias: null,
        size: selectedCertificates.length,
      },
    };

    const preloadedCertificateIds: PreloadedCertificateIds = {
      preloadedCertificateIds: selectedCertificates,
    };

    const dialogRef = this._dialog.open(DialogPendingCertDeleteComponent, dialogConfig);
    this._deleteCertsAfterCloseDialog(dialogRef, preloadedCertificateIds).subscribe();
  }

  public onDeleteIndividualCert(certificateId: number, certificateAlias: string): void {
    const dialogConfig: MatDialogConfig = {
      width: '450px',
      disableClose: true,
      data: {
        alias: certificateAlias,
        size: 1,
      },
    };

    const preloadedCertificateIds: PreloadedCertificateIds = {
      preloadedCertificateIds: [certificateId],
    };

    const dialogRef = this._dialog.open(DialogPendingCertDeleteComponent, dialogConfig);
    this._deleteCertsAfterCloseDialog(dialogRef, preloadedCertificateIds).subscribe();
  }

  private _deleteCertsAfterCloseDialog(
    dialogRef: MatDialogRef<any>,
    preloadedCertificateIds: PreloadedCertificateIds
  ): Observable<PagedTableResponse<PreloadedCertificate>> {
    return dialogRef.afterClosed().pipe(
      filter((res: boolean) => res),
      switchMap(_ => this._deletePreloadedCertificate(preloadedCertificateIds)),
      concatMap(_ => {
        this._resetPageIndex();
        this._preloadedCertsRequest.blockNumber = 0;
        return this._getPreloadedCertificates(this._preloadedCertsRequest);
      })
    );
  }

  // QUERIES
  private _getPreloadedCertificates(
    preloadCertsRequest: PagedTableRequest
  ): Observable<PagedTableResponse<PreloadedCertificate>> {
    this.sendingInfo = true;
    return this._preloadedCertificates.getPreloadedCertificates(preloadCertsRequest).pipe(
      map((res: Response<PagedTableResponse<PreloadedCertificate>>) => res.data),
      tap((res: PagedTableResponse<PreloadedCertificate>) => {
        this.dataSource.data = res.items;
        this.rowsCount = res.totalItems;
        this.scrollReset();
      }),
      finalize(() => {
        this.sendingInfo = false;
        this.loadingInfo = false;
      })
    );
  }

  private _deletePreloadedCertificate(
    deletePreloadCertsRequest: PreloadedCertificateIds
  ): Observable<Response<Array<number>>> {
    this.sendingInfo = true;

    return this._preloadedCertificates.deletePreloadedCertificate(deletePreloadCertsRequest).pipe(
      tap((res: Response<Array<number>>) => this._notify.feedBackResult(res.messageType, res.message, showSuccess)),
      finalize(() => {
        this.sendingInfo = false;
        this.selection.clear();
      })
    );
  }

  private _getUserPreference(): Observable<PreferenceResponse> {
    return this._userServ.getUserPreference(this._basePreferenceRequest).pipe(
      map((res: Response<PreferenceResponse>) => res.data),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  private _setUserPreference(preferenceRequest: PreferenceRequest): Observable<PreferenceResponse> {
    return this._userServ.setUserPreference(preferenceRequest).pipe(
      map((res: Response<PreferenceResponse>) => res.data),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }
}
